import { useCallback } from 'react';
import { useAuth } from 'oidc-react';
import { ApiFetchParams, getQueryFn, useApiQuery } from '../helpers/useApi';
import { apiFetch } from './fetch';
import { DOWNLOADER_API_PATH } from './reportOverview';

export function useGeneratedReports<T>(queryKey: string, searchTerm?: string) {
  return useApiQuery<T>(
    {
      path: `${DOWNLOADER_API_PATH}/reports${searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : ''}`,
      body: {},
    } as ApiFetchParams,
    `${queryKey}:${searchTerm}`
  );
}

export function useDeleteGeneratedReport() {
  const auth = useAuth();
  const maybeAccessToken = auth?.userData?.access_token;

  return useCallback(
    (reportId: string) =>
      getQueryFn<object>(
        {
          path: `${DOWNLOADER_API_PATH}/reports/${reportId}`,
          method: 'DELETE',
          body: {},
        },
        (
          body: unknown = {},
          path = '/',
          method = 'GET',
          endpoint: string | undefined = process.env.REACT_APP_API_GATEWAY,
          authorization: string | undefined = `Bearer ${maybeAccessToken}`
        ) => apiFetch<object>(path, body, method, endpoint, authorization)
      )(),
    [maybeAccessToken]
  );
}

interface DownloadReportResponse {
    reportUrl: string;
}

export interface ScheduleRequestBody {
  name: string;
  reportTypeId: number;
  startDate: string;
  endDate: string;
  recurrence: string;
  timezone: string;
  locationIds: number[];
  locationAreaIds: number[];
  staffIds: number[];
  tenderTypeIds: number[];
  deviceIds: number[];
  fileType: string;
}

export function downloadGeneratedReport(reportId: string, maybeAccessToken?: string) {

    return getQueryFn<DownloadReportResponse>(
        {
            path: `${DOWNLOADER_API_PATH}/reports/${reportId}/download`,
            method: 'GET',
            body: {},
        },
        (
            body: unknown = {},
            path = '/',
            method = 'GET',
            endpoint: string | undefined = process.env.REACT_APP_API_GATEWAY,
            authorization: string | undefined = `Bearer ${maybeAccessToken}`
        ) => apiFetch<DownloadReportResponse>(path, body, method, endpoint, authorization)
    )().then(({ reportUrl }) => {
        const link = document.createElement('a');
        link.href = reportUrl;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    });
}

export function toggleGeneratedReportEnabled(scheduleId: string, isEnabled: boolean, maybeAccessToken?: string) {

  return getQueryFn<object>(
    {
      path: `${DOWNLOADER_API_PATH}/reports/schedules/${scheduleId}/enabled`,
      method: 'PUT',
      body: { isEnabled },
    },
    (
      body: unknown = {},
      path = '/',
      method = 'GET',
      endpoint: string | undefined = process.env.REACT_APP_API_GATEWAY,
      authorization: string | undefined = `Bearer ${maybeAccessToken}`
    ) => apiFetch<object>(path, body, method, endpoint, authorization)
  )();
}

export function createReportSchedule(schedule: ScheduleRequestBody, maybeAccessToken?: string) {
  return getQueryFn<object>(
    {
      path: `${DOWNLOADER_API_PATH}/reports/schedules`,
      method: 'POST',
      body: schedule,
    },
    (
      body: unknown = {},
      path = '/',
      method = 'GET',
      endpoint: string | undefined = process.env.REACT_APP_API_GATEWAY,
      authorization: string | undefined = `Bearer ${maybeAccessToken}`
    ) => apiFetch<object>(path, body, method, endpoint, authorization)
  )();
}

export function updateReportSchedule(scheduleId: string, schedule: Partial<ScheduleRequestBody>, maybeAccessToken?: string) {
  return getQueryFn<object>(
    {
      path: `${DOWNLOADER_API_PATH}/reports/schedules/${scheduleId}`,
      method: 'PATCH',
      body: schedule,
    },
    (
      body: unknown = {},
      path = '/',
      method = 'GET',
      endpoint: string | undefined = process.env.REACT_APP_API_GATEWAY,
      authorization: string | undefined = `Bearer ${maybeAccessToken}`
    ) => apiFetch<object>(path, body, method, endpoint, authorization)
  )();
}
